<template>
  <div class="customers">
    <div class="page__header">
      <div class="page__title">{{ $t(`pageTitles.${"potential"}`) }} </div>
      <div class="page__right">
        <div class="selected-filters"
          v-if="selectedTeams.length > 0 || Object.keys(searchPotentialCustomerForms).length > 0">
          <div class="selected-filters__filter" v-for="teamId in selectedTeams" :key="`team${teamId}`">
            <span>
              Team: {{
                (loggedUser.teams && loggedUser.teams.find(t => t.id == teamId))
                  ? loggedUser.teams.find(t => t.id == teamId).name
                  : 'Team ' + teamId
              }}
            </span>
            <div class="selected-filters__remove" @click="removeTeam(teamId)"></div>
          </div>

          <div class="selected-filters__filter" v-if="startDateFrom || startDateTo">
            <span>{{ $t(`searchCustomerFields.${"policyStartDate"}`) }}: {{ formatDate(startDateFrom) || '...' }} - {{
              formatDate(startDateTo) || '...' }}</span>
            <div class="selected-filters__remove" @click="clearStartDateRange()"></div>
          </div>

          <div class="selected-filters__filter" v-if="endDateFrom || endDateTo">
            <span>{{ $t(`searchCustomerFields.${"policyEndDate"}`) }}: {{ formatDate(endDateFrom) || '...' }} - {{
              formatDate(endDateTo) || '...' }}</span>
            <div class="selected-filters__remove" @click="clearEndDateRange()"></div>
          </div>

          <div class="selected-filters__filter" v-if="birthday">
            <span>{{ $t('searchCustomerFields.birthday') }}: {{ birthday }}</span>
            <div class="selected-filters__remove" @click="clearBirthday()"></div>
          </div>

          <div class="selected-filters__filter" v-if="zipFrom || zipTo">
            <span>{{ $t('searchCustomerFields.zip') }}: {{ formatRange(zipFrom, zipTo) }}</span>
            <div class="selected-filters__remove" @click="clearZip()"></div>
          </div>

          <div class="selected-filters__filter" v-for="agentId in selectedAgents" :key="`agent${agentId}`">
            <span>
              {{
                (searchPotentialCustomerForms.agents && searchPotentialCustomerForms.agents.find(a => a.id == agentId))
                  ? searchPotentialCustomerForms.agents.find(a => a.id == agentId).first_name + ' ' +
                  searchPotentialCustomerForms.agents.find(a => a.id == agentId).last_name
                  : 'Agent ' + agentId
              }}
            </span>
            <div class="selected-filters__remove" @click="removeAgent(agentId)"></div>
          </div>


          <div class="selected-filters__filter" v-for="typeId in selectedCompanyTypes" :key="`type${typeId}`">
            <span>{{
              (searchPotentialCustomerForms.companyTypes && searchPotentialCustomerForms.companyTypes.find(item => item.id === typeId))
                ? searchPotentialCustomerForms.companyTypes.find(item => item.id === typeId).type
                : 'Type ' + typeId
            }}</span>
            <div class="selected-filters__remove" @click="removeCompanyType(typeId)"></div>
          </div>


          <div class="selected-filters__filter" v-for="categoryId in selectedBranchCategories"
            :key="`category${categoryId}`">
            <span>{{
              (searchPotentialCustomerForms.branchCategories && searchPotentialCustomerForms.branchCategories.find(item => item.id === categoryId))
                ? searchPotentialCustomerForms.branchCategories.find(item => item.id === categoryId)[`long_${language}`]
                : 'Category ' + categoryId
            }}</span>
            <div class="selected-filters__remove" @click="removeBranchCategory(categoryId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="priorityId in selectedPriorities" :key="`priority${priorityId}`">
            <span>{{
              (searchPotentialCustomerForms.priorities && searchPotentialCustomerForms.priorities.find(item => item.id === priorityId))
                ? searchPotentialCustomerForms.priorities.find(item => item.id === priorityId).type
                : 'Priority ' + priorityId
            }}</span>
            <div class="selected-filters__remove" @click="removePriority(priorityId)"></div>
          </div>

        </div>
        <form class="search search--filter" @submit.prevent="updateQuery()">
          <div class="search__wrapper">
            <input class="search__search" ref="search" :class="{ 'search__search--active': searchKeywords }" type="text"
              v-model="searchKeywords" />
            <div class="search__icon"></div>
            <div class="selected-filters__filter search__placeholder" v-if="searchPlaceholder">
              <div class="search__placeholder-content">
                {{ searchPlaceholder }}
                <div class="selected-filters__remove" @click="removeSearch()"></div>
              </div>
            </div>
            <div class="search__splitter"></div>
          </div>
          <div class="search__filter" ref="filterToggler" @click="showSearchFilter = true"></div>
          <SearchFilter v-if="showSearchFilter" :filterToggler="filterToggler" :searchType="'potentialCustomer'"
            :toggleSearchFilter="(state) => {
                showSearchFilter = state;
              }
              " />
        </form>
        <button type="button" class="button button--default button--animated"
          @click="store.commit('setShowSearchPotentialCustomerModal', true)">
          <div class="button__icon"></div>
        </button>
      </div>
    </div>
    <div class="customers__content">  
      <Loading v-if="customersLoading" />
      <PotentialPoliciesTable v-else :customers="customersPerPage.data || []" @refresh="searchCustomers" />
    </div>
    <Pagination :limit="3" :data="customersPerPage" @pagination-change-page="getResults"></Pagination>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import PotentialPoliciesTable from "@/components/PotentialPoliciesTable";
import Loading from "@/partials/Loading";
import SearchFilter from "@/partials/SearchFilter";

const store = useStore();
const route = useRoute();
const router = useRouter();
const instance = getCurrentInstance();

// Template refs
const search = ref(null);
const filterToggler = ref(null);

// Reactive data
const searchPlaceholder = ref("");
const searchKeywords = ref("");
const showSearchFilter = ref(false);
const selectedTeams = ref([]);
const selectedAgents = ref([]);
const selectedBranchCategories = ref([]);
const selectedCompanyTypes = ref([]);
const selectedPriorities = ref([]);
const startDateFrom = ref("");
const startDateTo = ref("");
const endDateFrom = ref("");
const endDateTo = ref("");
const birthday = ref("");
const zipFrom = ref("");
const zipTo = ref("");

// Computed properties
const customersPerPage = computed(() => store.getters.customersPerPage);
const customersLoading = computed(() => store.getters.customersLoading);
const searchPotentialCustomerForms = computed(() => store.getters.searchPotentialCustomerForms);
const language = computed(() => store.getters.language);
const loggedUser = computed(() => store.getters.loggedUser);

// Methods
const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

const formatRange = (from, to) => {
  if (!from && !to) return '';
  if (from && !to) return `${from} - ...`;
  if (!from && to) return `... - ${to}`;
  return `${from} - ${to}`;
};

const loadFiltersFromQuery = (q) => {
  searchKeywords.value = q.search || "";
  selectedTeams.value = q.teams ? q.teams.split(",").map(Number) : [];
  selectedAgents.value = q.agents ? q.agents.split(",").map(Number) : [];
  selectedBranchCategories.value = q.branchCategories ? q.branchCategories.split(",").map(Number) : [];
  selectedCompanyTypes.value = q.companyTypes ? q.companyTypes.split(",").map(Number) : [];
  selectedPriorities.value = q.priorities ? q.priorities.split(",").map(Number) : [];
  startDateFrom.value = q.startDateFrom || "";
  startDateTo.value = q.startDateTo || "";
  endDateFrom.value = q.endDateFrom || "";
  endDateTo.value = q.endDateTo || "";
  birthday.value = q.birthday || "";
  zipFrom.value = q.zip_from || "";
  zipTo.value = q.zip_to || "";
};

const searchCustomers = () => {
  store.commit("setCustomersLoading", true);

  const page = Number(route.query.page) || 1;
  const teamsFromUrl = route.query.teams;
  const teams = teamsFromUrl ? teamsFromUrl.split(",").map(Number) : [];

  const payload = {
    search: searchKeywords.value,
    page: page,
    teams: teams,
    agents: selectedAgents.value,
    branchCategories: selectedBranchCategories.value,
    companyTypes: selectedCompanyTypes.value,
    priorities: selectedPriorities.value,
    startDateFrom: startDateFrom.value,
    startDateTo: startDateTo.value,
    endDateFrom: endDateFrom.value,
    endDateTo: endDateTo.value,
    zip_from: zipFrom.value,
    zip_to: zipTo.value,
    birthday: birthday.value
  };

  store.dispatch("searchPotentialCustomers", payload)
    .then(() => {
      if (customersPerPage.value.data && customersPerPage.value.data.length === 0 && page > 1) {
        getResults(page - 1);
      }
    })
    .catch(error => {
      console.error("Search failed:", error);
    })
    .finally(() => {
      store.commit("setCustomersLoading", false);
    });
};

const updateQuery = () => {
  router.replace({
    query: {
      page: 1,
      search: searchKeywords.value || undefined,
      teams: selectedTeams.value.length ? selectedTeams.value.join(",") : undefined,
      agents: selectedAgents.value.length ? selectedAgents.value.join(",") : undefined,
      branchCategories: selectedBranchCategories.value.length ? selectedBranchCategories.value.join(",") : undefined,
      companyTypes: selectedCompanyTypes.value.length ? selectedCompanyTypes.value.join(",") : undefined,
      priorities: selectedPriorities.value.length ? selectedPriorities.value.join(",") : undefined,
      startDateFrom: startDateFrom.value || undefined,
      startDateTo: startDateTo.value || undefined,
      endDateFrom: endDateFrom.value || undefined,
      endDateTo: endDateTo.value || undefined,
      birthday: birthday.value || undefined,
      zip_from: zipFrom.value || undefined,
      zip_to: zipTo.value || undefined,
    },
  }).catch(() => { });
};

const clearAllDependentFilters = () => {
  selectedAgents.value = [];
  selectedBranchCategories.value = [];
  selectedCompanyTypes.value = [];
  selectedPriorities.value = [];
  birthday.value = "";
  zipFrom.value = "";
  zipTo.value = "";
  startDateFrom.value = "";
  startDateTo.value = "";
  endDateFrom.value = "";
  endDateTo.value = "";

  store.commit("setSearchPotentialCustomerForms", {});
};

const clearStartDateRange = () => {
  startDateFrom.value = "";
  startDateTo.value = "";
  updateQuery();
};

const clearEndDateRange = () => {
  endDateFrom.value = "";
  endDateTo.value = "";
  updateQuery();
};

const clearBirthday = () => {
  birthday.value = "";
  updateQuery();
};

const clearZip = () => {
  zipFrom.value = "";
  zipTo.value = "";
  updateQuery();
};

const removeTeam = (id) => {
  selectedTeams.value = selectedTeams.value.filter((x) => x !== id);

  if (selectedTeams.value.length === 0) {
    clearAllDependentFilters();
  }

  updateQuery();
};

const removeAgent = (id) => {
  selectedAgents.value = selectedAgents.value.filter((x) => x !== id);
  updateQuery();
};

const removeCompanyType = (id) => {
  selectedCompanyTypes.value = selectedCompanyTypes.value.filter((x) => x !== id);
  updateQuery();
};

const removeBranchCategory = (id) => {
  selectedBranchCategories.value = selectedBranchCategories.value.filter((x) => x !== id);
  updateQuery();
};

const removePriority = (id) => {
  selectedPriorities.value = selectedPriorities.value.filter((x) => x !== id);
  updateQuery();
};

const removeSearch = () => {
  searchKeywords.value = "";
  updateQuery();
};

const getResults = (page) => {
  if (Number(route.query.page) === Number(page)) return;

  router.replace({
    query: { ...route.query, page: page },
  });
};

// Watchers
watch(
  () => route.query,
  (query) => {
    if (!query.page) {
      router.replace({ query: { ...query, page: 1 } });
      return;
    }
    loadFiltersFromQuery(query);
    searchCustomers();
  },
  { deep: true, immediate: true }
);

// Lifecycle
onMounted(async () => {
  const hasAccess = instance.proxy.$checkRolePermission("customer_access") || instance.proxy.$checkRolePermission("customer_show");

  if (!hasAccess) {
    router.push("/dashboard");
    return;
  }

  try {
    const queryTeams = route.query.teams ? route.query.teams.split(",").map(Number) : [];

    if (queryTeams.length > 0) {
      await store.dispatch("getSearchPotentialCustomerForms", queryTeams);
    } else if (loggedUser.value.teams && loggedUser.value.teams.length === 1) {
      await store.dispatch("getSearchPotentialCustomerForms", [loggedUser.value.teams[0].id]);
    }
  } catch (error) {
    console.error("Error loading potential customer forms:", error);
  }
});
</script>

<style scoped lang="scss">
.customers {
  height: 100%;

  &__content {
    margin-bottom: 20px;
    height: calc(100% - 48px - 15px - 16px - 20px);
  }
}
</style>
